import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices, reorderChoices } from '../utils/helpers';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface CheckboxFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [showOther, setShowOther] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const normalizedChoices = normalizeChoices(element.choices);
  const choices = reorderChoices(normalizedChoices, element.choicesOrder);
  const hasOtherOption = element.showOtherItem;

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || []}
        render={({ field }) => {
          const values: string[] = Array.isArray(field.value)
            ? field.value
            : [];

          const handleChange = (choiceValue: string, checked: boolean) => {
            if (checked) {
              field.onChange([...values, choiceValue]);
            } else {
              field.onChange(values.filter((v) => v !== choiceValue));
            }
            formState.markFieldAsTouched(element.name);
            formState.clearValidationError(element.name);
          };

          const handleOtherChange = (checked: boolean) => {
            setShowOther(checked);
            if (!checked) {
              field.onChange(values.filter((v) => !v.startsWith('__other__:')));
              setOtherValue('');
            }
          };

          const handleOtherTextChange = (text: string) => {
            setOtherValue(text);
            const newValues = values.filter((v) => !v.startsWith('__other__:'));
            if (text) {
              newValues.push(`__other__:${text}`);
            }
            field.onChange(newValues);
          };

          return (
            <div className={styles.checkboxGroup}>
              {choices.map((choice, index) => (
                <label key={index} className={styles.checkboxOption}>
                  <input
                    type="checkbox"
                    checked={values.includes(choice.value)}
                    onChange={(e) =>
                      handleChange(choice.value, e.target.checked)
                    }
                    disabled={element.readOnly}
                  />
                  <span>{choice.text}</span>
                </label>
              ))}
              {hasOtherOption && (
                <>
                  <label className={styles.checkboxOption}>
                    <input
                      type="checkbox"
                      checked={showOther}
                      onChange={(e) => handleOtherChange(e.target.checked)}
                      disabled={element.readOnly}
                    />
                    <span>{element.otherText || 'Outro'}</span>
                  </label>
                  {showOther && (
                    <input
                      type="text"
                      className={`${styles.input} ${styles.otherInput}`}
                      placeholder={
                        element.otherPlaceholder || 'Digite sua resposta'
                      }
                      value={otherValue}
                      onChange={(e) => handleOtherTextChange(e.target.value)}
                      disabled={element.readOnly}
                    />
                  )}
                </>
              )}
            </div>
          );
        }}
      />
      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default CheckboxField;
